import type { Viewer } from '../permissions/viewer';
import type { GateContext } from '../permissions/guards';
import { CAPABILITY_LABELS, isCapabilityKey, type CapabilityKey } from '../permissions/capabilityKeys';
import { fill, t } from '../strings';

/**
 * File release (piece 9; decisions 0068, 0072, 0085): a row naming one
 * outside company, offered greyed with the reason rather than
 * selectable-then-warned, refused rather than warned when the source account's
 * NDA does not permit the target, and released by the deal owner or a manager.
 */
export type ReleasePermissionValue = 'View' | 'Download';

export type ReleaseOfferReason = 'Customer' | 'ActiveNda' | 'NeedsSignedNda';

export interface ReleaseOffer {
  companyId: string;
  selectable: boolean;
  reason: ReleaseOfferReason;
  label: string;
}

export const NEEDS_SIGNED_NDA = 'Needs a signed NDA';

/** The customer needs no NDA; a third party needs an Active agreement that enables release, else it is greyed with the reason. */
export function releaseTargetOffer(companyId: string, facts: { isCustomer: boolean; hasActiveNdaEnablingRelease: boolean }): ReleaseOffer {
  if (facts.isCustomer) return { companyId, selectable: true, reason: 'Customer', label: '' };
  if (facts.hasActiveNdaEnablingRelease) return { companyId, selectable: true, reason: 'ActiveNda', label: '' };
  return { companyId, selectable: false, reason: 'NeedsSignedNda', label: NEEDS_SIGNED_NDA };
}

export interface SourceCover {
  requiresCover: boolean;
  counterpartyName: string;
  counterpartyCompanyId: string;
  permittedRecipientCompanyIds: readonly string[];
}

/**
 * The target's standing on the server (decision 4601): whether it is one of the file's own companies, the
 * Deal's account and its contacts' companies or, for a file on no Deal, its account; and the offer the dialogue drew.
 */
export interface ReleaseTarget {
  companyId: string;
  name: string;
  ownCompany: boolean;
  /** Where the file's own companies come from, so the refusal names the right ones (T2597): a Deal's, the one account, or none. Absent reads as a Deal's. */
  scope?: 'deal' | 'account' | 'none';
  offer: Pick<ReleaseOffer, 'selectable'>;
}

/**
 * 🔒 Refused rather than warned (decision 4601): a target the source account's NDA does not permit, a company outside
 * the file's own companies, and a third party with no Active NDA enabling release. Null when the release stands.
 * "Acme Controls requires cover on this account. Superior Panel Co is not a permitted recipient under Acme's NDA."
 */
export function releaseRefusal(source: SourceCover | null, target: ReleaseTarget): string | null {
  const cover = coverRefusal(source, target);
  if (cover !== null) return cover;
  if (!target.ownCompany) return notOwnCompanyRefusal(target);
  if (!target.offer.selectable) return `${target.name} needs a signed NDA that enables release before a file is released to it.`;
  return null;
}

/** 🔒 The wording names only the companies this file can be released to: a Deal's account and contacts, the one account it sits on, or none (T2597). */
function notOwnCompanyRefusal(target: Pick<ReleaseTarget, 'name' | 'scope'>): string {
  if (target.scope === 'none') return 'This file sits on no account, so it is released to no company.';
  if (target.scope === 'account') return `${target.name} is not the account this file sits on, so the file is not released to it.`;
  return `${target.name} is not the file's account or a Deal contact's company, so the file is not released to it.`;
}

/** The source account's NDA, read first: its counterparty and its permitted recipients alone receive the file. */
function coverRefusal(source: SourceCover | null, target: { companyId: string; name: string }): string | null {
  if (source === null || !source.requiresCover) return null;
  if (target.companyId === source.counterpartyCompanyId) return null;
  if (source.permittedRecipientCompanyIds.includes(target.companyId)) return null;
  const short = source.counterpartyName.split(' ')[0] ?? source.counterpartyName;
  return `${source.counterpartyName} requires cover on this account. ${target.name} is not a permitted recipient under ${short}'s NDA.`;
}

/** The ladder: ticking Download ticks and disables View, since download-but-not-view is not a representable state. */
export function permissionLadder(download: boolean): { view: true; download: boolean; viewDisabled: boolean } {
  return { view: true, download, viewDisabled: download };
}

export interface ReleaseSettings {
  defaultPermission: ReleasePermissionValue;
  downloadOffered: boolean;
  watermarkText: string | null;
  approvalRuleId: string | null;
}

/** What the dialogue reads until Settings and administration seeds the four `files.release*` rows (found while building, 2026-09-08). */
export const RELEASE_SETTING_DEFAULTS: ReleaseSettings = { defaultPermission: 'View', downloadOffered: true, watermarkText: null, approvalRuleId: null };

export const RELEASE_SETTING_KEYS = {
  defaultPermission: 'files.releaseDefaultPermission',
  downloadOffered: 'files.releaseDownloadOffered',
  watermarkText: 'files.releaseWatermarkText',
  approvalRuleId: 'files.releaseApprovalRuleId',
} as const;

export function releaseSettingsFrom(rows: Readonly<Record<string, unknown>>): ReleaseSettings {
  const perm = rows[RELEASE_SETTING_KEYS.defaultPermission];
  const offered = rows[RELEASE_SETTING_KEYS.downloadOffered];
  const text = rows[RELEASE_SETTING_KEYS.watermarkText];
  const rule = rows[RELEASE_SETTING_KEYS.approvalRuleId];
  return {
    defaultPermission: perm === 'Download' ? 'Download' : RELEASE_SETTING_DEFAULTS.defaultPermission,
    downloadOffered: typeof offered === 'boolean' ? offered : RELEASE_SETTING_DEFAULTS.downloadOffered,
    watermarkText: typeof text === 'string' && text.trim().length > 0 ? text : null,
    approvalRuleId: typeof rule === 'string' && rule.length > 0 ? rule : null,
  };
}

/**
 * 🔒 Decision 10340 (T801): the capability a file no owner answers for is released under, labelled
 * *Releases a file nobody owns*, seeded on Admin and Accountant.
 */
export const RELEASE_UNOWNED_CAPABILITY = 'files.releaseUnowned' satisfies CapabilityKey;

/** 🔒 Decision 10340: the rule that named a file's release owner, first that answers: the Deal's owner, the account's rep, the record's own owner, else the capability. */
export type ReleaseOwnerRule = 'dealOwner' | 'accountRep' | 'recordOwner' | 'capability';

/** What a refusal carries so a window can draw it: the rule, the owner's name where a person answered, and the key where none did. */
export interface ReleaseOwnerFacts {
  rule: ReleaseOwnerRule;
  name: string | null;
  capability: string | null;
}

/**
 * 🔒 The release owner's sentence (decision 10340), through `t()`: the owner it found and the rule that named them, or
 * the capability's label. An answer missing its name or carrying an unknown key still reads as a sentence.
 */
export function releaseOwnerSentence(owner: Partial<ReleaseOwnerFacts> | null | undefined): string {
  const name = typeof owner?.name === 'string' && owner.name.length > 0 ? owner.name : '—';
  switch (owner?.rule) {
    case 'dealOwner': return fill(t('files.release.owner.dealOwner'), { name });
    case 'accountRep': return fill(t('files.release.owner.accountRep'), { name });
    case 'recordOwner': return fill(t('files.release.owner.recordOwner'), { name });
    default: {
      const key = isCapabilityKey(owner?.capability) ? owner.capability : RELEASE_UNOWNED_CAPABILITY;
      return fill(t('files.release.owner.capability'), { capability: t(CAPABILITY_LABELS[key]), key });
    }
  }
}

/** The release owner a refusal's body carries, or null where the body is not one (decision 10340). */
export function releaseOwnerOf(body: unknown): ReleaseOwnerFacts | null {
  const owner = typeof body === 'object' && body !== null ? (body as { releaseOwner?: unknown }).releaseOwner : null;
  if (typeof owner !== 'object' || owner === null) return null;
  const { rule, name, capability } = owner as Record<string, unknown>;
  if (rule !== 'dealOwner' && rule !== 'accountRep' && rule !== 'recordOwner' && rule !== 'capability') return null;
  return { rule, name: typeof name === 'string' ? name : null, capability: typeof capability === 'string' ? capability : null };
}

/** Only the deal owner or a manager releases; release is a commercial decision that does not follow document class. */
export function releaseAllowedFor(viewer: Pick<Viewer, 'personId'>, facts: { dealOwnerPersonId: string | null; viewerIsManager: boolean; holdsUnownedCapability?: boolean }, ctx: GateContext): boolean {
  if (ctx.mode === 'sandbox') return true;
  // 🔒 Decision 10340: a file no owner answers for is released by a holder of files.releaseUnowned; with an owner the key grants nothing.
  if (facts.dealOwnerPersonId === null && facts.holdsUnownedCapability === true) return true;
  return facts.viewerIsManager || (facts.dealOwnerPersonId !== null && facts.dealOwnerPersonId === viewer.personId);
}

/**
 * 🔒 The approval states in which a release opens its file (decision 8960): Not required, a file that is not
 * Confidential, and Approved. A Pending or Rejected release grants nothing, and a revoked one nothing either.
 */
export const RELEASE_IN_FORCE_APPROVALS = ['NotRequired', 'Approved'] as const;

export function releaseInForce(row: { revokedAt: Date | string | null; approvalStatus: string }): boolean {
  return row.revokedAt === null && (RELEASE_IN_FORCE_APPROVALS as readonly string[]).includes(row.approvalStatus);
}

/** 🔒 What a File Release asks, in words, which leads its routing sentence and its queue row (decisions 1484, 8960). */
export function releaseAsk(fileName: string, companyName: string, reason: string | null): string {
  const asked = `Release ${fileName} to ${companyName}`;
  return reason && reason.trim().length > 0 ? `${asked}: ${reason.trim()}` : asked;
}

/** A Confidential file is released with a reason and an approval through the existing Approval Workflow. */
export function confidentialReleaseNeeds(file: { confidentialityRoleId: string | null }, settings: Pick<ReleaseSettings, 'approvalRuleId'>): { reason: boolean; approval: boolean } {
  const confidential = file.confidentialityRoleId !== null;
  return { reason: confidential, approval: confidential || settings.approvalRuleId !== null };
}
